import React, { useEffect, useCallback } from "react";
import { CSVLink } from "react-csv";
import FuzzySearch from "fuzzy-search";
import { withStyles } from "@mui/styles";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import PasswordIcon from "@mui/icons-material/Password";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import TableSortLabel from "@mui/material/TableSortLabel";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Box, TextField } from "@mui/material";
import { format, subMonths, subWeeks } from "date-fns";

import Label from "../label/Label";
import SingleLineTextInput from "../../components/singleLineTextInput/SingleLineTextInput";
import Button from "../../components/button/Button";
import DropdownSelect from "../../components/dropdownSelect/DropdownSelect";

const TableHeader = styled("div")(({ theme }) => ({
  marginBottom: "20px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const Buttons = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& > div": {
    marginLeft: 5,
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    "& > div": {
      marginLeft: 0,
      marginTop: 10,
    },
  },
}));

const styles = (theme) => ({
  tableWrapper: {
    width: "100%",
    overflow: "auto",
  },
  table: {
    width: "100%",
    borderSpacing: "0px",
  },
  tableHeading: {
    color: "#7B7B7B",
    borderBottom: "1px solid #D7D7D7",
  },
  tableCell: {
    borderBottom: "1px solid #D7D7D7",
    paddingTop: 10,
    paddingBottom: 10,
    fontSize: 13,
  },
  tableActionsCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  buttons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& > div": {
      marginLeft: 5,
    },
  },
  yesValue: {
    backgroundColor: "#00823E",
    width: 100,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
  },
  noValue: {
    backgroundColor: "lightgrey",
    width: 100,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  filterSection: {
    marginBottom: 20,
    display: "flex",
    overflowY: "scroll",
  },
  filterSelects: {
    display: "flex",
  },
  filterSelect: {
    width: 150,
    marginRight: 10,
  },
  cellTag: {
    marginLeft: 5,
    background: "#00823e",
    padding: 5,
    color: "#fff",
  },
});

const AlwaysVisibleSortLabel = styled(TableSortLabel)(({ theme }) => ({
  "& .MuiTableSortLabel-icon": {
    opacity: 0.3, // faded look for inactive sort
  },
  "&.Mui-active .MuiTableSortLabel-icon": {
    opacity: 1, // full opacity for active sort
  },
}));

function DataTable({
  classes,
  headings,
  data,
  onRowClick,
  addButtonText,
  onAddButtonClick,
  rowLink,
  hideControls,
  hideSearch,
  rowActions,
  additionalButtons,
  showDateRangeFilter,
  dateRangeFilterStart,
  onDateRangeFilterStartChange,
  dateRangeFilterEnd,
  onDateRangeFilterEndChange,
  filterSelects,
  showDownloadButton,
  downloadFilename,
}) {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [sortBy, setSortBy] = React.useState("");
  const [sortDirection, setSortDirection] = React.useState("");
  const [csvData, setCSVData] = React.useState([]);

  function filterData({ searchTerm, data, headings }) {
    let keys = [];
    for (let i = 0; i < headings.length; i++) {
      const heading = headings[i];

      if (heading.key && !heading.subKey) {
        keys.push(heading.key);
      } else if (heading.key && heading.subKey) {
        keys.push(`${heading.key}.${heading.subKey}`);
      }
    }

    const searcher = new FuzzySearch(data, keys, {
      caseSensitive: false,
    });
    const result = searcher.search(searchTerm);

    return result;
  }

  const sortData = useCallback(
    (data) => {
      return [...data].sort((a, b) => {
        const valueA = a[sortBy];
        const valueB = b[sortBy];

        if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
        if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
        return 0; // values are equal
      });
    },
    [sortBy, sortDirection]
  );

  function renderTableHeadings() {
    console.log("sortDirection2", sortDirection);

    const handleSort = (heading) => {
      console.log("sortBy", sortBy, heading.key, sortDirection);
      const isActive = sortBy === heading.key;
      if (isActive) {
        // Toggle sort direction
        setSortDirection((prevDirection) =>
          prevDirection === "asc" ? "desc" : "asc"
        );
      } else {
        // Set new sort key and default to ascending
        setSortBy(heading.key);
        setSortDirection("asc");
      }
    };

    if (headings) {
      return headings.map((heading) => {
        if (typeof heading.label === "string") {
          const isActive = sortBy === heading.key;

          console.log("sortDirection", sortDirection);

          return (
            <TableCell
              key={"heading-" + heading.key}
              className={classes.tableHeading}
              sortDirection={isActive ? sortDirection : false}
            >
              <AlwaysVisibleSortLabel
                active={isActive}
                direction={isActive ? sortDirection : "asc"}
                onClick={() => handleSort(heading)}
              >
                {heading.label}
              </AlwaysVisibleSortLabel>
            </TableCell>
          );
        }
        return false;
      });
    }
    return false;
  }

  function getIcon(type) {
    switch (type) {
      case "edit": {
        return <EditIcon />;
      }

      case "delete": {
        return <DeleteIcon />;
      }

      case "email": {
        return <EmailIcon />;
      }

      case "resetPassword": {
        return <PasswordIcon />;
      }

      case "clearMandate": {
        return <MoneyOffIcon />;
      }

      default: {
        return <EditIcon />;
      }
    }
  }

  function renderTableActions({ rowIndex, dataItem, rowActions }) {
    return (
      rowActions &&
      rowActions
        .filter(
          (rowAction) =>
            rowAction.onClick &&
            (!rowAction.hidden || !rowAction.hidden(dataItem.data))
        )
        .map((rowAction) => {
          return (
            <Tooltip title={rowAction.label}>
              <div key={`action-${rowIndex}-${rowAction.type}`}>
                <IconButton
                  aria-label="delete"
                  onClick={() => rowAction.onClick(dataItem.data)}
                >
                  {getIcon(rowAction.type)}
                </IconButton>
              </div>
            </Tooltip>
          );
        })
    );
  }

  function renderAdditionalButtons({ additionalButtons }) {
    return additionalButtons.map((additionalButton) => {
      return (
        <div>
          <Button
            title={additionalButton.title}
            onClick={(payload) => additionalButton.onPress(payload)}
          />
        </div>
      );
    });
  }

  function transformData({ data, headings }) {
    return data.map((dataItem) => {
      let transformedDataItem = {
        data: dataItem,
      };

      headings.forEach((heading, i) => {
        let value = "";

        if (heading.type === "timeRange") {
          value = `${dataItem[heading.startKey]} - ${dataItem[heading.endKey]}`;
          return (transformedDataItem[heading.key] = value);
        }

        let dataValue = dataItem[heading.key];
        if (typeof dataValue === "object" && dataValue) {
          if (heading.subKey) {
            dataValue = dataValue[heading.subKey];
          } else {
            dataValue = JSON.stringify(dataValue);
          }
        }

        if (typeof heading.key === "string") {
          if (
            heading.type === "currency" &&
            dataValue !== null &&
            dataValue !== undefined
          ) {
            value = `£${dataValue.toFixed(2)}`;
          } else if (heading.type === "yesNo") {
            if (dataValue) {
              value = "Yes";
            } else {
              value = "No";
            }
          } else if (heading.type === "date" && dataValue) {
            if (dataValue.toString() === "0001-01-01T00:00:00") {
              return null;
            }
            value = format(new Date(dataValue), "dd/MM/yyyy");
          } else {
            value = dataValue;
          }
        } else {
          value = dataValue;
        }

        if (heading.subKey) {
          return (transformedDataItem[heading.key] = {
            ...transformedDataItem[heading.key],
            [heading.subKey]: value,
          });
        }
        return (transformedDataItem[heading.key] = value);
      });

      return transformedDataItem;
    });
  }

  function renderTableRows() {
    if (data) {
      let transformedData = sortData(transformData({ data, headings }));
      return filterData({ searchTerm, data: transformedData, headings }).map(
        (dataItem, i) => {
          return (
            <TableRow key={"row-" + i}>
              {headings.map((heading, i) => {
                let value = null;

                if (typeof heading.key === "string") {
                  if (heading.type === "yesNo") {
                    if (dataItem[heading.key] === "Yes") {
                      return (
                        <TableCell
                          onClick={() =>
                            onRowClick && onRowClick(dataItem.data.id)
                          }
                          key={"cell-" + i}
                          className={classes.tableCell}
                        >
                          <span className={classes.yesValue}>
                            {dataItem[heading.key]}
                          </span>
                        </TableCell>
                      );
                    } else {
                      return (
                        <TableCell
                          onClick={() =>
                            onRowClick && onRowClick(dataItem.data.id)
                          }
                          key={"cell-" + i}
                          className={classes.tableCell}
                        >
                          <span className={classes.noValue}>
                            {dataItem[heading.key]}
                          </span>
                        </TableCell>
                      );
                    }
                  } else {
                    value = dataItem[heading.key];
                  }
                } else if (heading.key.constructor === Array) {
                  value = dataItem[heading.key[0]];
                  for (let i = 1; i < heading.key.length; i++) {
                    if (value) {
                      value = value[heading.key[i]];
                    } else {
                      return <td key={"cell-" + i} />;
                    }
                  }
                }

                if (heading.subKey) {
                  value = dataItem[heading.key][heading.subKey];
                }

                if (!value) {
                  return (
                    <TableCell
                      onClick={() => onRowClick && onRowClick(dataItem.data.id)}
                      key={"cell-" + i}
                      className={classes.tableCell}
                    ></TableCell>
                  );
                }

                return (
                  <TableCell
                    onClick={() => onRowClick && onRowClick(dataItem.data.id)}
                    key={"cell-" + i}
                    className={classes.tableCell}
                  >
                    {value.toString()}{" "}
                    {heading.tag && heading.tag(dataItem.data) && (
                      <span className={classes.cellTag}>
                        {heading.tag(dataItem.data)}
                      </span>
                    )}
                  </TableCell>
                );
              })}

              {!hideControls && (
                <TableCell className={classes.tableCell} align="right">
                  <div className={classes.tableActionsCell}>
                    {renderTableActions({ rowIndex: i, dataItem, rowActions })}
                  </div>
                </TableCell>
              )}
            </TableRow>
          );
        }
      );
    }
    return false;
  }

  const generateCSV = useCallback(() => {
    let buildCSVData = [];

    buildCSVData.push(headings.map((heading) => heading.label));

    let transformedData = sortData(transformData({ data, headings }));
    let filterDataRaw = filterData({
      searchTerm,
      data: transformedData,
      headings,
    });

    filterDataRaw.forEach((dataItem) => {
      buildCSVData.push(
        headings.map((heading) => {
          if (heading.subKey) {
            return dataItem[heading.key][heading.subKey];
          }
          return dataItem[heading.key];
        })
      );
    });

    setCSVData(buildCSVData);
  }, [searchTerm, data, headings, sortData]);

  useEffect(() => {
    if (showDownloadButton) {
      generateCSV();
    }
  }, [data, generateCSV, showDownloadButton]);

  const quickSelects = [
    { label: "1 week ago", value: subWeeks(new Date(), 1) },
    { label: "1 month ago", value: subMonths(new Date(), 1) },
    { label: "3 months ago", value: subMonths(new Date(), 3) },
    { label: "6 months ago", value: subMonths(new Date(), 6) },
    { label: "12 months ago", value: subMonths(new Date(), 12) },
  ];

  return (
    <div style={{ marginTop: 10 }}>
      <TableHeader>
        <div>
          {!hideSearch && !hideControls && (
            <SingleLineTextInput
              placeholder="Search..."
              style={{ marginRight: 0, marginBottom: 0 }}
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
            />
          )}
          {/* <IconButton /> */}
        </div>
        <Buttons>
          <div>
            {addButtonText && !hideControls && onAddButtonClick && (
              <Button
                title={addButtonText}
                onClick={(payload) => onAddButtonClick(payload)}
              />
            )}
          </div>
          {showDownloadButton && (
            <div style={{ marginLeft: 5 }}>
              <CSVLink filename={`${downloadFilename}.csv`} data={csvData}>
                <Button title="Download report" />
              </CSVLink>
            </div>
          )}
          {additionalButtons && (
            <div>{renderAdditionalButtons({ additionalButtons })}</div>
          )}
        </Buttons>
      </TableHeader>
      {(showDateRangeFilter || (filterSelects && filterSelects.length > 0)) && (
        <div className={classes.filterSection}>
          {showDateRangeFilter && (
            <div style={{ marginRight: 10 }}>
              <Label title={"Start date"} />
              <DatePicker
                value={dateRangeFilterStart}
                onChange={(value) => onDateRangeFilterStartChange(value)}
                style={{ marginRight: 10 }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
              <Box
                mt={1}
                display="flex"
                gap={1}
                flexWrap="wrap"
                width={"240px"}
              >
                {quickSelects.map((option) => (
                  <div
                    style={{
                      textDecorationLine: "underline",
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                    onClick={() => onDateRangeFilterStartChange(option.value)}
                  >
                    {option.label}
                  </div>
                ))}
              </Box>
            </div>
          )}

          {showDateRangeFilter && (
            <div style={{ marginRight: 10 }}>
              <Label title={"End date"} />
              <DatePicker
                value={dateRangeFilterEnd}
                onChange={(value) => onDateRangeFilterEndChange(value)}
                style={{ marginRight: 10 }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </div>
          )}
          {filterSelects &&
            filterSelects.map((filterSelect) => (
              <div
                key={`filter-${filterSelect.label}`}
                className={classes.filterSelect}
              >
                <DropdownSelect
                  label={filterSelect.label}
                  value={filterSelect.value}
                  items={filterSelect.items}
                  onSelect={(value) => filterSelect.onSelect(value)}
                  placeholder={filterSelect.placeholder}
                />
              </div>
            ))}
        </div>
      )}
      <div className={classes.tableWrapper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {renderTableHeadings()}
              <TableCell className={classes.tableCell}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableRows()}</TableBody>
        </Table>
      </div>
    </div>
  );
}

export default withStyles(styles)(DataTable);
